import React from 'react';
import * as mobx from 'mobx';
import { action, observable } from 'mobx';
import { IEventRequestParams } from '../../../../../api/shared.dto';
import * as IoC from 'typescript-ioc';
import {
    Category,
    IConfigurationFilter,
    IReportConfigItem,
    IReportConfiguration,
    IReportsDAO,
    ISelectableFilter,
    LocalConfigurationStore,
    ReportConfigurationTarget,
    ReportLayout,
    ReportType
} from '../../../../../api/reports.dao';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { getShortTextLengthError } from '../../../../../infrastructure/utils/form-validation.utils';
import Text from '../../../../global/text/text';
import { REPORT_FIELD_CATEGORY_GROUPS } from '../../../../../constants/report-fields.constants';
import { FormattedMessage } from 'react-intl';

export class ReportConfigurationViewModel {
    @observable
    public currentConfiguration: IReportConfigItem[] = [];
    public customFields: IReportConfigItem[] = [];
    @observable
    public eventColumns: IReportConfigItem[] = [];
    @observable
    public configurationLabel = '';
    @mobx.observable
    public isGenerating = false;
    @mobx.observable
    public isSavingNewConfiguration = false;
    @mobx.observable
    public isLoadingSavedConfigurations = false;
    @mobx.observable
    public configurations: IReportConfiguration[];
    @mobx.observable
    public isSavingEnabled: boolean = true;
    @mobx.observable
    public configurationTarget: ReportConfigurationTarget = ReportConfigurationTarget.USER;
    @mobx.observable
    public searchValue = '';
    @mobx.observable
    public appliedFilters: IConfigurationFilter[] = [];
    @mobx.observable
    public isAddingNewModalOpen: boolean = false;
    @mobx.observable
    public isSubmitted: boolean = false;
    @mobx.observable
    public layoutType: ReportLayout = ReportLayout.ROW_PER_ATTENDEE;
    @mobx.observable
    public layoutSpecificFields: IReportConfigItem[] = [];
    @mobx.observable
    public isLoadingLayoutFields = false;

    public selectedConfigurationName: string;
    public selectedConfigurations: IReportConfiguration[];

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        public readonly type: ReportType,
        public readonly excludedCategories: Category[],
        public readonly availableFilterTypes: ISelectableFilter[],
        private clearMeetingDates?: () => void,
        private generateEventReport?: (
            currentConfiguration: IReportConfigItem[],
            configurationName: string,
        ) => Promise<void>,
        private readonly localStorageName?: LocalConfigurationStore,
        public readonly withLayout?: boolean,
        @IoC.Inject private readonly reportsDAO = IoC.Container.get(IReportsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) { }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private static getFieldIndexById(fieldId: string, fieldList: IReportConfigItem[]): number {
        return fieldList.findIndex(field => field.fieldId === fieldId);
    }

    private static markFieldAsEnabled(field: IReportConfigItem): IReportConfigItem {
        return {
            ...field, enabled: true
        };
    }
    private static markFieldAsDisabled(field: IReportConfigItem): IReportConfigItem {
        return {
            ...field, enabled: false
        };
    }

    public async initialize(): Promise<void> {
        let columns: IReportConfigItem[] = [];
        let responseConfig = null;
        try {
            mobx.runInAction(() => {
                this.isLoadingLayoutFields = true;
            });

            if (this.type === ReportType.ATTENDEE_INTERACTIONS_REPORT) {
                const responseConfigAttendeeMeetingLayout = await this.reportsDAO.getDefaultEventReportConfiguration({
                    ...this.eventReqParams,
                    type: this.type
                });

                const responseConfigAttendeeLayout = await this.reportsDAO.getDefaultEventReportConfiguration({
                    ...this.eventReqParams,
                    type: 'ATTENDEE_INTERACTIONS_REPORT_V2'
                });

                const responseConfigUnitesLayouts = responseConfigAttendeeMeetingLayout.concat(responseConfigAttendeeLayout);

                let tempSet = new Set();
                responseConfig = responseConfigUnitesLayouts.filter(item => {
                    if (!tempSet.has(item.fieldId)) {
                        tempSet.add(item.fieldId);
                        return item;
                    }
                });

            } else {
                responseConfig = await this.reportsDAO.getDefaultEventReportConfiguration({
                    ...this.eventReqParams,
                    type: this.type === ReportType.REGISTRANTS_BY_SESSION_REPORT ? ReportType.EVENT_INTERACTIONS_REPORT : this.type

                });
            }

            /* sorting disabled fields */
            const firstDisabledIndex = this.getFirstDisabledFieldIndex(responseConfig);

            if (firstDisabledIndex === -1) {
                columns = responseConfig;
            } else {
                const disabledFields = responseConfig.splice(firstDisabledIndex);
                disabledFields.sort((a, b) => a.label.localeCompare(b.label));
                columns = responseConfig.concat(disabledFields);
            }

            columns = columns.filter(el => !this.excludedCategories.includes(el.category));
            mobx.runInAction(() => {
                this.eventColumns = columns;
                this.layoutSpecificFields = columns;
                this.customFields = this.eventColumns.filter(el => el.customField);
            });
            this.fetchLayoutFields();
        } catch (e) {
            ReportConfigurationViewModel.handleApiException(e);
        } finally {
            if (this.localStorageName) {
                this.loadConfigurationFromStorage();
            } else {
                this.applyCurrentConfiguration(this.eventColumns);
            }
            mobx.runInAction(() => {
                this.isLoadingLayoutFields = false;
            });
        }
    }

    public saveConfiguration = async () => {
        mobx.runInAction(() => {
            this.isSubmitted = true;
        });

        if (this.getConfigurationLabelError()) return;

        try {
            mobx.runInAction(() => {
                this.isSavingNewConfiguration = true;
            });
            const response = await this.reportsDAO.saveConfiguration({
                ...this.eventReqParams,
                name: this.configurationLabel.trim(),
                type: this.type,
                columns: this.getFormattedColumnsForSave(),
                filters: this.getAppliedFiltersForSave(),
                target: this.configurationTarget,
                ...( this.withLayout && { layoutType: this.layoutType }),
            });

            mobx.runInAction(() => {
                this.configurations = [
                    ...this.configurations,
                    response
                ];
                this.selectedConfigurationName = response.name;
            });

            this.trackSaveNewConfigurationMixPanel();
        } catch (e) {
            ReportConfigurationViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.resetFormValues();
            });
        }
    };

    private resetFormValues = (): void => {
        this.configurationLabel = '';
        this.isSavingNewConfiguration = false;
        this.isAddingNewModalOpen = false;
        this.isSubmitted = false;
    };

    @mobx.action
    public getSavedConfigurations = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isLoadingSavedConfigurations = true;
            });
            const configurations = await this.reportsDAO.getConfigurations({
                ...this.eventReqParams,
                type: this.type
            });
            mobx.runInAction(() => {
                this.configurations = configurations;
            });
        } catch (e) {
            ReportConfigurationViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoadingSavedConfigurations = false;
            });
        }
    };

    private setColumnsOrder = (configurationColumns: IReportConfigItem[]): IReportConfigItem[] => {
        const firstDisabledIndex = this.getFirstDisabledFieldIndex(configurationColumns);
        const enabledFields = configurationColumns.slice(0, firstDisabledIndex);
        const disabledFields = configurationColumns.slice(firstDisabledIndex);

        const columns = new Set(
            enabledFields
                .concat(this.customFields)
                .concat(disabledFields)
        );

        return [...columns];
    };

    public onSelectConfiguration = (columns: IReportConfigItem[], filters: IConfigurationFilter[] = [], layoutType = ReportLayout.ROW_PER_ATTENDEE) => {
        const orderedColumns = this.setColumnsOrder(columns);
        const formattedColumns = this.getFormattedColumnsForLoad(orderedColumns);
        this.applyCurrentConfiguration(formattedColumns);
        this.applyFilters(filters);
        this.applyLayout(layoutType);
        this.clearMeetingDates?.();
    };

    public resetConfiguration = () => {
        this.applyCurrentConfiguration(this.eventColumns);
    };

    @mobx.action
    public onConfigurationsSelectionChange = (configurations: IReportConfiguration[]) => {
        if (this.selectedConfigurationName !== configurations[0]?.name) {
            this.setConfigurationLabel('');
            this.selectedConfigurationName = configurations[0]?.name;
        }
        this.isSavingEnabled = !configurations.find(el => el.target === this.configurationTarget);
        this.selectedConfigurations = configurations;
    };

    public getItems(): IReportConfigItem[] {
        return this.currentConfiguration;
    }

    public getEnabledItems(): IReportConfigItem[] {
        return this.getAvailableColumns().filter(el => el.enabled);
    }

    private isFieldAvailable = (fieldId: string) => this.layoutSpecificFields.find(el => fieldId === el.fieldId);

    public getGroupedFilteredItems(): { groupName: string; items: IReportConfigItem[] }[] {
        const sortedItems = this.getItems().slice().sort((a, b) => a.label.localeCompare(b.label));
        const filteredItems = sortedItems.filter(el => el.label.toLowerCase().includes(this.searchValue.toLowerCase()));

        return REPORT_FIELD_CATEGORY_GROUPS.filter(group => !this.excludedCategories.includes(group.category)).map(group => {
            const items = sortedItems.filter(el => el.category === group.category && this.isFieldAvailable(el.fieldId));
            const areAnyFieldsInCategory = items.some(el => this.isFieldAvailable(el.fieldId));
            if (!areAnyFieldsInCategory) return null;

            return {
                groupName: group.name,
                items: filteredItems.filter(el => el.category === group.category && this.isFieldAvailable(el.fieldId))
            };
        }).filter(Boolean);
    }

    public getGroupedFiltersList(filters: ISelectableFilter[]): { groupName: string; items: ISelectableFilter[]}[] {
        // This function returns grouped filters by field.category, even if category is excluded but there is filter isRequired===true
        // category will be included in the result array with only this filter
        const fields = this.getItems();
        const sortedFilters = filters.slice().sort((a, b) => a.label.localeCompare(b.label));
        const filtersWithCategory = sortedFilters.map(filter => ({...filter, category: fields.find(el => el.fieldId === filter.field)?.category || ''}));

        const excludedCategoriesWithoutRequired = this.excludedCategories.filter(category => {
            return !filtersWithCategory.find(filter => filter.isRequired && filter.category === category);
        });

        return REPORT_FIELD_CATEGORY_GROUPS
            .filter(group => group.category !== Category.CUSTOM && !excludedCategoriesWithoutRequired.includes(group.category))
            .map(group => ({
                groupName: group.name,
                items: filtersWithCategory.filter(filter =>
                    filter.category === group.category && (this.isFieldAvailable(filter.field) || filter.isRequired)
                )
        }));
    }

    @mobx.action
    public setSearchValue = (value: string) => {
        this.searchValue = value;
    };

    @action.bound
    public reorder(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();

        if (config[sourceIndex].enabled) {
            this.reorderEnabledField(sourceIndex, destinationIndex);
        } else {
            this.reorderDisabledField(sourceIndex, destinationIndex);
        }
    }

    @action
    public move(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();

        if (config[sourceIndex].enabled) {
            this.moveEnabledField(sourceIndex, destinationIndex);
        } else {
            this.moveDisabledField(sourceIndex, destinationIndex);
        }
    }

    @action
    public enableField(field: IReportConfigItem): void {
        const enabledFields = this.addFieldToEnabledList(field);
        const disabledFields = this.removeFieldFromDisabledList(field);
        const config = [...enabledFields, ...disabledFields];

        this.applyCurrentConfiguration(config);
    }

    @action
    public disableField(field: IReportConfigItem): void {
        const enabledFields = this.removeFieldFromEnabledList(field);
        const disabledFields = this.addFieldToDisabledList(field);
        const config = [...enabledFields, ...disabledFields];

        this.applyCurrentConfiguration(config);
    }

    public toggleField = (field: IReportConfigItem) => {
        if (field.enabled) {
            this.disableField(field);
        } else {
            this.enableField(field);
        }
    };

    @mobx.action
    public setConfigurationLabel = (value: string): void => {
        this.configurationLabel = value;
    };

    @mobx.action
    public onChangeConfigurationStore = (value: ReportConfigurationTarget): void => {
        this.configurationTarget = value;
        this.isSavingEnabled = !this.selectedConfigurations.find(el => el.target === value);
    };

    @mobx.action
    public setAddingNewModalOpen = (): void => {
        this.isAddingNewModalOpen = true;
    };

    @mobx.action
    public setAddingNewModalClose = (): void => {
        this.resetFormValues();
    };

    public getConfigurationLabelError = (): JSX.Element | null => {
        if (!this.configurationLabel.trim()) {
            return <FormattedMessage
                defaultMessage='Report Configuration Name is required'
                id='oe.reports.reportConfigurationNameIsRequired'
                description='Error message in popover'
            />;
        }

        if (getShortTextLengthError('Configuration Label', this.configurationLabel)) {
            return <FormattedMessage
                defaultMessage={getShortTextLengthError('Configuration Label', this.configurationLabel)}
                id='oe.reports.configurationLabelError'
                description='Error message text'
            />;
        }

        if (!this.isSavingEnabled) {
            return <FormattedMessage
                defaultMessage='This configuration already exists'
                id='oe.reports.reportConfigurationAlreadyExists'
                description='Error message in popover'
            />;
        }

        return null;
    };

    private getFirstDisabledFieldIndex(configuration: IReportConfigItem[] = this.currentConfiguration): number {
        return configuration.map(field => field.enabled).indexOf(false);
    }

    @mobx.action
    public onCustomize = async (): Promise<boolean> => {
        this.isGenerating = true;
        try {
            await this.generateEventReport(
                this.currentConfiguration,
                this.selectedConfigurationName,
            );
            return true;
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error)
            });
        } finally {
            mobx.runInAction(() => {
                this.isGenerating = false;
            });
        }
    };

    private getPopulatedSavedConfigurationWithNewProperty = (property: string, columns: IReportConfigItem[]) => {
        const result = [...columns];

        columns.forEach((el, i) => {
            const eventColumn = this.eventColumns.find(({ fieldId }) => el.fieldId === fieldId);
            if (eventColumn) {
                result[i] = {
                    ...result[i],
                    [property]: eventColumn[property]
                };
            }
        });

        return result;
    };

    private getFormattedColumnsForLoad = (configurationColumns: IReportConfigItem[]): IReportConfigItem[] => {
        let columns = configurationColumns;
        if (configurationColumns.length && !('category' in configurationColumns[0])) {
            columns = this.getPopulatedSavedConfigurationWithNewProperty('category', configurationColumns);
        }

        const restColumns = this.eventColumns.filter(eventColumn => {
            return !columns.some(column => eventColumn.fieldId === column.fieldId);
        });

        const newOrderIndex = columns[columns.length - 1].orderIndex + 1;
        return [
            ...columns,
            ...restColumns.map((el, i) => ({
                ...el,
                enabled: false,
                orderIndex: newOrderIndex + i
            }))
        ];
    };

    private getFormattedColumnsForSave = (): IReportConfigItem[] => {
        const columnsWithoutCustomFields = this.getAvailableColumns().filter(el => !el.customField);
        return columnsWithoutCustomFields.map((el, i) => ({
            ...el,
            orderIndex: i
        }));
    };

    public addConfigurationToStorage = () => {
        const columns = this.getFormattedColumnsForSave();
        localStorage.setItem(this.localStorageName, JSON.stringify({
            columns: columns,
            filters: this.getAppliedFiltersForSave(),
            ...( this.withLayout && { layoutType: this.layoutType }),
        }));
    };

    private loadConfigurationFromStorage = () => {
        const configurationString = localStorage.getItem(this.localStorageName);

        try {
            type SavedConfigurationType = Pick<IReportConfiguration, 'columns' | 'filters' | 'layoutType'> | IReportConfigItem[];
            const configuration: SavedConfigurationType = JSON.parse(configurationString);
            const columns = 'columns' in configuration ? configuration.columns : configuration;
            const filters = 'filters' in configuration ? configuration.filters : [];
            const layoutType = 'layoutType' in configuration ? configuration.layoutType : ReportLayout.ROW_PER_ATTENDEE;

            this.onSelectConfiguration([...columns, ...this.customFields], filters, layoutType);
        } catch {
            this.resetConfiguration();
        }
    };

    private trackSaveNewConfigurationMixPanel = () => {
        this.trackingService.get().track(TRACKING_EVENTS.SAVE_NEW_CONFIGURATION, {
            'Configuration name': this.selectedConfigurationName
        });
    };

    private moveEnabledField(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();
        const field = config[sourceIndex];

        if (destinationIndex !== 0 && !config[destinationIndex - 1].enabled) {
            this.disableField(field);
            return;
        }

        if (sourceIndex < destinationIndex) {
            config.splice(destinationIndex, 0, field);
            config.splice(sourceIndex, 1);
        } else {
            config.splice(sourceIndex, 1);
            config.splice(destinationIndex, 0, field);
        }

        this.applyCurrentConfiguration(config);
    }

    private moveDisabledField(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();

        if (!config[destinationIndex].enabled) return;
        const field = ReportConfigurationViewModel.markFieldAsEnabled(config[sourceIndex]);
        config.splice(sourceIndex, 1);
        config.splice(destinationIndex, 0, field);

        this.applyCurrentConfiguration(config);
    }

    private reorderEnabledField(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();
        const field = config[sourceIndex];

        if (config[destinationIndex].enabled) {
            config.splice(sourceIndex, 1);
            config.splice(destinationIndex, 0, field);
            this.applyCurrentConfiguration(config);
        } else {
            this.disableField(field);
        }
    }

    private reorderDisabledField(sourceIndex: number, destinationIndex: number): void {
        const config = this.getConfigCopy();
        if (!config[destinationIndex].enabled) return;

        const field = ReportConfigurationViewModel.markFieldAsEnabled(config[sourceIndex]);
        config.splice(sourceIndex, 1);
        config.splice(destinationIndex, 0, field);
        this.applyCurrentConfiguration(config);
    }

    @action
    private applyCurrentConfiguration(columns: IReportConfigItem[]): void {
        this.currentConfiguration = columns.map((field, i) => ({ ...field, orderIndex: i }));
    }

    private addFieldToEnabledList(field: IReportConfigItem): IReportConfigItem[] {
        const enabledField = ReportConfigurationViewModel.markFieldAsEnabled(field);
        const enabledFields = this.getEnabledFields();
        enabledFields.push(enabledField);

        return enabledFields;
    }
    private removeFieldFromEnabledList(field: IReportConfigItem): IReportConfigItem[] {
        const enabledFields = this.getEnabledFields();
        const fieldIndex = ReportConfigurationViewModel.getFieldIndexById(field.fieldId, enabledFields);

        if (fieldIndex !== -1) enabledFields.splice(fieldIndex, 1);

        return enabledFields;
    }

    private addFieldToDisabledList(field: IReportConfigItem): IReportConfigItem[] {
        const disabledField = ReportConfigurationViewModel.markFieldAsDisabled(field);
        const disabledFields = this.getDisabledFields();

        disabledFields.push(disabledField);
        disabledFields.sort((a, b) => a.label.localeCompare(b.label));

        return disabledFields;
    }
    private removeFieldFromDisabledList(field: IReportConfigItem): IReportConfigItem[] {
        const disabledFields = this.getDisabledFields();
        const fieldIndex = ReportConfigurationViewModel.getFieldIndexById(field.fieldId, disabledFields);

        if (fieldIndex !== -1) disabledFields.splice(fieldIndex, 1);

        return disabledFields;
    }

    private getEnabledFields(): IReportConfigItem[] {
        const firstDisabledIndex = this.getFirstDisabledFieldIndex();

        return firstDisabledIndex === -1
            ? this.getConfigCopy()
            : this.currentConfiguration.slice(0, firstDisabledIndex);
    }

    private getDisabledFields(): IReportConfigItem[] {
        const firstDisabledIndex = this.getFirstDisabledFieldIndex();
        return firstDisabledIndex === -1
            ? []
            : this.currentConfiguration.slice(firstDisabledIndex);
    }

    private getConfigCopy(): IReportConfigItem[] {
        return this.currentConfiguration.slice();
    }

    private get eventReqParams(): IEventRequestParams {
        return { accountId: this.accountId, eventId: this.eventId };
    }

    public showErrorModal = error => {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    };

    public deleteConfigurationApiCall = async (id: number) => {
        try {
            await this.reportsDAO.deleteConfiguration({
                ...this.eventReqParams,
                reportConfigurationTemplateId: id
            });
            mobx.runInAction(() => {
                this.configurations = this.configurations
                    .filter(config => config.reportConfigurationTemplateId !== id);
            });
        } catch (error) {
            this.showErrorModal(error);
        }
    };

    public deleteConfiguration = (id: number) => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSureYouWishTo'
                        description='Alert message'
                        message='Are you sure you wish to '
                    />
                    <Text
                        className='alert-message--danger'
                        id='oe.eventReportConfiguration.deleteThisConfiguration'
                        description='Alert message'
                        message='delete this configuration?'
                    />
                </>
            ),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: () => this.deleteConfigurationApiCall(id),
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public applyFilters = (filters: IConfigurationFilter[]) => {
        this.appliedFilters = filters;
    };

    @mobx.action
    public applyFilter = (filter: IConfigurationFilter) => {
        const updatedFilters = [...this.appliedFilters];

        const filterIndex = updatedFilters.findIndex(el => el.field === filter.field);
        if (filterIndex !== -1) {
            updatedFilters.splice(filterIndex, 1, filter);
        } else {
            updatedFilters.push(filter);
        }

        this.appliedFilters = updatedFilters;
    };

    @mobx.action
    public spliceFilter = (field: string): IConfigurationFilter[] => {
        const updatedFilters = [...this.appliedFilters];

        const filterIndex = updatedFilters.findIndex(el => el.field === field);
        updatedFilters.splice(filterIndex, 1);
        return updatedFilters;
    };

    @mobx.action
    public clearFilter = (field: string) => {
        const updatedFilters = this.spliceFilter(field);
        this.appliedFilters = updatedFilters;
    };

    @mobx.action
    public clearAllFilters = () => {
        this.appliedFilters = [];
    };

    public getAppliedFilter = (field: string): IConfigurationFilter | undefined => {
        return this.appliedFilters.find(el => el.field === field);
    };

    public getAppliedFiltersForSave = (): IConfigurationFilter[] => {
        return this.getAppliedFilters().filter(filter => {
            const filterConfiguration = this.availableFilterTypes.find(el => el.field === filter.field);
            return !filterConfiguration.excludeFromSave;
        });
    };

    public getUpdatedFilters = (updatedFilters: IConfigurationFilter[]): IConfigurationFilter[] => {
        return updatedFilters.filter(filter => {
            const { isRequired } = this.availableFilterTypes.find(el => el.field === filter.field);
            return isRequired || this.layoutSpecificFields.find(field => field.fieldId === filter.field);
        });
    };

    public getAppliedFilters = (): IConfigurationFilter[] => {
        return this.getUpdatedFilters(this.appliedFilters);
    };

    public getAvailableColumns = () => {
        return this.currentConfiguration.filter(el => this.layoutSpecificFields.find(field => field.fieldId === el.fieldId));
    };

    @mobx.action
    public applyLayout = (value: ReportLayout) => {
        if (this.layoutType !== value) {
            this.layoutType = value;
            this.fetchLayoutFields();
        }
    };

    public async fetchLayoutFields(): Promise<void> {
        if (this.type !== ReportType.ATTENDEE_INTERACTIONS_REPORT) return null;
        try {
            mobx.runInAction(() => {
                this.isLoadingLayoutFields = true;
            });
            const responseConfig = await this.reportsDAO.getDefaultEventReportConfiguration({
                ...this.eventReqParams,
                type: this.layoutType === ReportLayout.ROW_PER_ATTENDEE ? 'ATTENDEE_INTERACTIONS_REPORT_V2' : ReportType.ATTENDEE_INTERACTIONS_REPORT
            });

            mobx.runInAction(() => {
                this.layoutSpecificFields = responseConfig;
            });
        } catch (e) {
            ReportConfigurationViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoadingLayoutFields = false;
            });
        }
    }
}
